/**
 * Escaping for text that goes into the landing page's markup.
 *
 * Most of what the page renders is authored in `landing-copy.ts` and is safe
 * by construction. These two are for what is not: the company name, the social
 * platforms and URLs, and the legal titles all come from the admin panel — a
 * database row a person edits — and they land in `innerHTML`. Core applies the
 * same rule to the admin-supplied meta tags, for the same reason.
 *
 * Its own module rather than a helper inside `index.ts` so that `legal-view.ts`
 * can use it without importing the whole landing page (and with it the copy,
 * the section markup and the CMS fetches) into a route that renders one
 * document.
 */

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}

/**
 * A URL going into an `href`.
 *
 * NOT a general attribute escaper despite the name (which is core's convention
 * and kept for it): it REPLACES anything that is not a URL, so passing it a
 * slug or a label silently yields '#'. For any other attribute value use
 * `escapeHtml`, which escapes the quotes and leaves the content alone.
 *
 * A `javascript:` URL in an admin-editable social link would run on click, and
 * escaping alone does not stop it: the quotes are safe and the scheme still
 * fires. So anything that is not plainly http(s), a same-origin path or a
 * fragment is REPLACED rather than sanitised. There is no legitimate third
 * scheme on this page, and a dead link is a better failure than an executable
 * one.
 */
export function escapeAttr(s: string): string {
  const safe = /^(https?:\/\/|\/|#)/i.test(s) ? s : '#';
  return escapeHtml(safe);
}
